import { useEffect, useState } from "react";
import { useOrganization } from "../../App";
import { slotService } from "@/services/slotService";
import { Slot } from "@/types/slot";
import Calendar from "@/components/calendar/calendar";
import { format, parseISO } from "date-fns";
import {
  BookedTimeRange,
  TimeRange,
  UserDefinedRange,
} from "@/lib/calendar-types";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

interface SlotCalendarViewProps {
  campaignId?: string;
  onSlotClick?: (slot: Date) => void;
  pendingSlotData?: any;
  onSlotCreated?: () => void;
  editingSlotId?: string;
  onSlotSegmentsUpdated?: () => void;
  isPublicBooking?: boolean;
  selectedTimeSlots?: TimeRange[];
  onRangesChange?: (ranges: TimeRange[]) => void;
  selectionMode?: "click-only" | "drag";
}

export function SlotCalendarView({
  campaignId,
  onSlotClick,
  pendingSlotData,
  onSlotCreated,
  editingSlotId,
  onSlotSegmentsUpdated,
  isPublicBooking,
  selectedTimeSlots: externalSelectedTimeSlots,
  onRangesChange: externalOnRangesChange,
  selectionMode,
}: SlotCalendarViewProps) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCalendarMode, setIsCalendarMode] = useState(false);
  const [pendingSlotDataState, setPendingSlotDataState] = useState<any>(null);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<TimeRange[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const { currentOrganization } = useOrganization();
  const { toast } = useToast();

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchSlots();
    }
  }, [currentOrganization, editingSlotId]);

  const fetchSlots = async () => {
    if (!currentOrganization?.id) return;

    setLoading(true);
    try {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 2, 0);

      const { data, error } = await slotService.getSlots(
        currentOrganization.id,
        {
          startDate: firstDay.toISOString(),
          endDate: lastDay.toISOString(),
        },
      );

      if (error) throw error;
      setSlots(data || []);
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast({
        title: "Error",
        description: "Failed to load slots",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToCalendar = (slotData: any) => {
    setPendingSlotDataState(slotData);
    setIsCalendarMode(true);
  };

  const handleCreateSlot = async (newSlot: any) => {
    if (!currentOrganization?.id) return;

    try {
      // Ensure the slot has an organization_id
      const slotWithOrgId = {
        ...newSlot,
        organization_id: currentOrganization.id
      };
      
      if (
        newSlot.recurrence_pattern &&
        newSlot.recurrence_pattern !== "none" &&
        newSlot.recurrence_end_date
      ) {
        // For recurring slots
        const recurringSlots = generateRecurringSlots(
          slotWithOrgId,
          newSlot.recurrence_pattern,
          newSlot.recurrence_end_date,
        );

        await slotService.createMultipleSlots(recurringSlots);
      } else {
        // For a single slot
        await slotService.createSlot(slotWithOrgId);
      }

      fetchSlots();
      toast({
        title: "Success",
        description: "Slot created successfully",
      });
    } catch (error) {
      console.error("Error creating slot:", error);
      toast({
        title: "Error",
        description: "Failed to create slot",
        variant: "destructive",
      });
    }
  };
  
  // Helper function to generate recurring slots
  const generateRecurringSlots = (baseSlot: any, pattern: string, endDate: string) => {
    // This is a placeholder for the actual implementation
    // In a real implementation, you would generate multiple slot objects based on the recurrence pattern
    return [baseSlot]; // For now, just return the base slot
  };

  const convertSlotsToBookedRanges = (): BookedTimeRange[] => {
    if (!slots || slots.length === 0) return [];

    // Filter out the slot being edited if editingSlotId is provided
    const slotsToConvert = editingSlotId
      ? slots.filter((slot) => slot.id !== editingSlotId)
      : slots;

    // Flatten all segments from filtered slots into a single array of BookedTimeRange objects
    return slotsToConvert.reduce<BookedTimeRange[]>((acc, slot) => {
      // Access segments directly from the slot data that now comes from the API
      const segments = slot.segments || [];

      // Map each segment to a BookedTimeRange
      const segmentRanges = segments.map((segment, index) => {
        try {
          if (!segment.start_time || !segment.end_time) {
            console.warn("Segment missing start or end time:", segment);
            return null;
          }

          const startDate = parseISO(segment.start_time);
          const endDate = parseISO(segment.end_time);

          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            console.error("Invalid date in segment:", segment);
            return null;
          }

          return {
            id: `${slot.id}-segment-${segment.id || index}`,
            title: slot.name || `Slot (${slot.filled_seats || 0}/${slot.capacity || 0})`,
            startDate,
            endDate,
            dayName: format(startDate, "EEEE"),
            bookedBy: `${slot.filled_seats || 0}/${slot.capacity || 0} booked`,
          };
        } catch (error) {
          console.error("Error converting segment to booked range:", error);
          return null;
        }
      });

      // Filter out any null results and add valid ranges to accumulator
      return [...acc, ...segmentRanges.filter(Boolean)];
    }, []);
  };

  // Convert segments of the editing slot to TimeRange objects
  const convertSegmentsToTimeRanges = (): TimeRange[] => {
    if (!editingSlotId) return []; // Return empty if not editing

    const editingSlot = slots.find((slot) => slot.id === editingSlotId);
    if (!editingSlot) return []; // Return empty if slot not found

    // Access segments from the slot data that's now included in the API response
    const segments = editingSlot.segments || [];

    const segmentRanges = segments.map((segment, index) => {
      try {
        if (!segment.start_time || !segment.end_time) {
          console.warn("Segment missing start or end time:", segment);
          return null;
        }

        const startDate = parseISO(segment.start_time);
        const endDate = parseISO(segment.end_time);

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.error("Invalid date in segment:", segment);
          return null;
        }

        return {
          id: `${editingSlot.id}-segment-${segment.id || index}`,
          startDate,
          endDate,
          dayName: format(startDate, "EEEE"),
        };
      } catch (error) {
        console.error("Error converting segment to time range:", error);
        return null;
      }
    });

    // Filter out any null results and return valid ranges
    return segmentRanges.filter(Boolean);
  };

  const handleTimeSlotSelect = (timeRange: TimeRange) => {
    setSelectedTimeSlots((prev) => [...prev, timeRange]);
    setHasPendingChanges(true);
  };

  const handleRangesChange = (ranges: TimeRange[]) => {
    // Make a deep copy to ensure we're not working with references
    const rangesCopy = JSON.parse(JSON.stringify(ranges));
    setSelectedTimeSlots(rangesCopy);

    // Only set hasPendingChanges to true if we're in edit mode and the ranges have changed
    if (editingSlotId) {
      const currentSegments = convertSegmentsToTimeRanges();
      // Compare the new ranges with the original segments
      const hasChanges =
        rangesCopy.length !== currentSegments.length ||
        JSON.stringify(
          rangesCopy.map((r) => ({ start: r.startDate, end: r.endDate })),
        ) !==
          JSON.stringify(
            currentSegments.map((r) => ({
              start: r.startDate,
              end: r.endDate,
            })),
          );
      setHasPendingChanges(hasChanges);
    } else {
      setHasPendingChanges(rangesCopy.length > 0);
    }
  };

  const handleSaveChanges = async () => {
    if (!currentOrganization?.id) return;

    try {
      // Convert selected time slots to segments
      const segments = selectedTimeSlots.map((slot) => ({
        start_time: slot.startDate.toISOString(),
        end_time: slot.endDate.toISOString(),
      }));

      // Ensure we have at least one segment
      if (segments.length === 0) {
        toast({
          title: "Error",
          description: "Please select at least one time slot",
          variant: "destructive",
        });
        return;
      }

      // Calculate overall start_time and end_time from segments
      const startTimes = segments.map(s => new Date(s.start_time).getTime());
      const endTimes = segments.map(s => new Date(s.end_time).getTime());
      const overallStartTime = new Date(Math.min(...startTimes)).toISOString();
      const overallEndTime = new Date(Math.max(...endTimes)).toISOString();

      // Create the slot data, using either pending data or defaults
      const slotData = pendingSlotDataState || {
        organization_id: currentOrganization.id,
        name: "New Slot",
        capacity: 10,
        time_limit: 30,
        status: "available",
        campaign_id: campaignId,
      };

      // Create the slot first with required start_time and end_time
      const newSlot = {
        ...(pendingSlotDataState || slotData),
        start_time: overallStartTime,
        end_time: overallEndTime,
      };
        
      const { data: createdSlot, error } = await slotService.createSlot(newSlot);
      if (error) throw error;
      
      // Now add segments to the created slot
      if (createdSlot && segments.length > 0) {
        for (const segment of segments) {
          await slotService.createSlotSegment({
            slot_id: createdSlot.id,
            start_time: segment.start_time,
            end_time: segment.end_time
          });
        }
      }
      
      setSelectedTimeSlots([]);
      setHasPendingChanges(false);
      setIsCalendarMode(false);

      if (onSlotCreated) {
        onSlotCreated();
      }
    } catch (error) {
      console.error("Error saving slot with segments:", error);
      toast({
        title: "Error",
        description: "Failed to save slot with selected slot segments",
        variant: "destructive",
      });
    }
  };

  const handleClearSegments = () => {
    setSelectedTimeSlots([]);
    setHasPendingChanges(false);
  };

  const handleUpdateSlotSegments = async () => {
    if (!editingSlotId || !currentOrganization?.id) return;

    try {
      // Get the current slot data
      const { data: slotData, error: slotError } =
        await slotService.getSlot(editingSlotId);
      if (slotError) throw slotError;

      // Convert selected time slots to segments
      const segments = selectedTimeSlots.map((slot) => {
        // Ensure startDate and endDate are Date objects
        const startDate =
          slot.startDate instanceof Date
            ? slot.startDate
            : new Date(slot.startDate);
        const endDate =
          slot.endDate instanceof Date ? slot.endDate : new Date(slot.endDate);

        return {
          start_time: startDate.toISOString(),
          end_time: endDate.toISOString(),
        };
      });

      // Delete existing segments
      const { error: deleteError } =
        await slotService.deleteSlotSegments(editingSlotId);
      if (deleteError) throw deleteError;

      // Create new segments
      const createdSegments = [];
      for (const segment of segments) {
        const { data: createdSegment, error: createError } =
          await slotService.createSlotSegment({
            slot_id: editingSlotId,
            start_time: segment.start_time,
            end_time: segment.end_time
          });
        if (createError) throw createError;
        if (createdSegment) createdSegments.push(createdSegment);
      }

      // Update the local state with the new segments
      const updatedTimeSlots = createdSegments.map((segment) => ({
        id: `${editingSlotId}-segment-${segment.id}`,
        startDate: new Date(segment.start_time),
        endDate: new Date(segment.end_time),
        dayName: format(new Date(segment.start_time), "EEEE"),
      }));

      setSelectedTimeSlots(updatedTimeSlots);

      toast({
        title: "Success",
        description: "Slot segments updated successfully",
      });

      setHasPendingChanges(false);
      fetchSlots();

      // Notify parent component that segments were updated
      if (onSlotSegmentsUpdated) {
        onSlotSegmentsUpdated();
      }
    } catch (error) {
      console.error("Error updating slot segments:", error);
      toast({
        title: "Error",
        description: "Failed to update slot segments",
        variant: "destructive",
      });
    }
  };

  // Initialize selectedTimeSlots with the slot's segments when in editing mode
  useEffect(() => {
    if (editingSlotId && slots.length > 0) {
      const segments = convertSegmentsToTimeRanges();
      setSelectedTimeSlots(segments);
      setHasPendingChanges(false); // Reset pending changes when initializing
    }
  }, [slots, editingSlotId]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">
          {isCalendarMode && pendingSlotData
            ? `Select Slot Segments for ${pendingSlotData.name || "New Slot"}`
            : editingSlotId
              ? `Slot Segments Calendar for ${slots.find((s) => s.id === editingSlotId)?.name || "Slot"}`
              : "Slot Segments Calendar"}
        </h2>
        {editingSlotId && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClearSegments}>
              Clear All Segments
            </Button>
            {hasPendingChanges && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedTimeSlots(convertSegmentsToTimeRanges());
                    setHasPendingChanges(false);
                  }}
                >
                  Cancel Changes
                </Button>
                <Button onClick={handleUpdateSlotSegments}>
                  Update Slot Segments
                </Button>
              </>
            )}
          </div>
        )}
        {!editingSlotId && hasPendingChanges && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedTimeSlots([]);
                setHasPendingChanges(false);
              }}
            >
              Cancel Changes
            </Button>
            <Button onClick={handleSaveChanges}>Save Changes</Button>
          </div>
        )}
      </div>

      {isCalendarMode && pendingSlotData && (
        <div className="bg-primary/10 p-4 rounded-lg mb-4 dark:bg-primary/20">
          <p className="text-sm font-medium">
            Slot Created: {pendingSlotData.name || "New Slot"}
          </p>
          <p className="text-sm">
            Duration: {pendingSlotData.time_limit} minutes
          </p>
          <p className="text-sm">Capacity: {pendingSlotData.capacity} people</p>
          <p className="text-sm text-muted-foreground mt-2">
            You can now view and manage this slot in the calendar
          </p>
        </div>
      )}

      <Calendar
        initialBookedRanges={convertSlotsToBookedRanges()}
        initialUserDefinedRanges={convertSegmentsToTimeRanges()}
        onBookTimeSlot={() => {}}
        onTimeSlotSelect={handleTimeSlotSelect}
        slotDuration={
          (pendingSlotData || pendingSlotDataState)?.time_limit || 30
        }
        selectedTimeSlots={selectedTimeSlots}
        onRangesChange={handleRangesChange}
        selectionMode={selectionMode}
        onSlotClick={onSlotClick}
        enforceRangeRestriction={false} // Allow selecting any available time slot
      />
    </div>
  );
}
